import {onCLS, onLCP, onTTFB, onINP} from 'web-vitals';
import Viewport from './viewport';

/**
 * Wrapper for using Google Web Vitals measuring API
 * https://github.com/GoogleChrome/web-vitals
 */
class WebVitals {
  /**
   * Con.
   */
  constructor() {
    /** @type {string|undefined} */
    this.analyticsUrl_ = '';
    this.analyticsBody_ = {};
    this.clsEntries = [];
    this.metricNeeded_ = ['LCP', 'TTFB', 'CLS', 'INP'];
    this.metricsSent_ = false; // send metrics only once
  }

  /**
   * Check if Vitals analytics is enabled by retrieving POST URL
   * @return {string|undefined}
   */
  getAnalyticsUrl() {
    return (window.ui && window.ui.webvitals) ? window.ui.webvitals.endpoint : '';
  }

  /**
   * Set analytics URL
   * @param {string} url
   */
  setAnalyticsUrl(url) {
    this.analyticsUrl_ = url;
  }

  /**
   * Set some initial values collected to analytics body
   * @param {string} deviceClass
   * @param {string} layoutClass
   */
  initBody(deviceClass, layoutClass) {
    this.analyticsBody_['deviceclass'] = deviceClass;
    this.analyticsBody_['layoutclass'] = layoutClass;
  }

  /**
   * Clears body, for testing purpose
   */
  resetBody() {
    this.analyticsBody_ = {};
    this.clsEntries = [];
    this.metricsSent_ = false;
  }

  /**
   * Handler for Google API
   * @param {Object} metric
   */
  collectLayoutShift(metric) {
    try {
      if (window.ui.webvitals.debugCls) {
        metric['entries'].slice(0, 10).forEach(entry => {
          if (entry && entry.sources) {
            this.clsEntries.push(this.convertLayoutShiftEntry(entry));
          }
        });
      }
    } finally {
      this.collectMetric(metric);
    }
  }

  /**
   * Handler for Google API
   * @param {Object} metric
   */
  collectTTFB(metric) {
    try {
      const first = metric['entries'][0];
      ['redirectStart', 'redirectEnd', 'domainLookupStart', 'domainLookupEnd', 'requestStart'].forEach(attr => {
        const attrValue = first[attr];
        if (attrValue) {
          this.analyticsBody_[attr] = attrValue.toFixed(3);
        }
      });
    } catch (e) {
      console.warn('Unable to add TTFB details', e);
    } finally {
      this.collectMetric(metric);
    }
  }

  /**
   * Converts layout shift entry into dto for backend.
   * @param {Object} entry
   * @return {Object}
   */
  convertLayoutShiftEntry(entry) {
    const sources = [];
    entry.sources.slice(0, 10).forEach(source => {
      sources.push(this.convertElementToIdentifer(source.node));
    });
    return {
      'value': entry.value,
      'sources': sources
    };
  }

  /**
   * Converts element to identifer / path.
   * @param {HTMLElement} element
   * @return {string}
   */
  convertElementToIdentifer(element) {
    try {
      const elementPath = [];
      for (let elm = element; elm; elm = /** @type HTMLElement */ (elm.parentNode)) {
        if (elm.nodeName === null) {
          continue;
        }
        let entry = elm.nodeName.toLowerCase();
        if (entry === 'html') {
          break;
        }

        if (elm.className) {
          entry += '.' + elm.className.replace(/ /g, '.');
        }
        elementPath.push(entry);
      }
      elementPath.reverse();
      return elementPath.join(' ');
    } catch (e) {
      console.error('Unable to create path', e);
      return 'error';
    }
  }

  /**
   * Handler for Google API
   * @param {Object} metric
   */
  collectMetric(metric) {
    console.log('[CWV]', metric);
    this.analyticsBody_[metric.name.toLowerCase()] = metric.value;
    if (!this.metricsSent_) {
      if (this.allMetricGot()) {
        this.sendToAnalytics();
        this.metricsSent_ = true;
      }
    }
  }

  /**
   * Check if all needed Vitals metrics are collected
   * @return {boolean}
   */
  allMetricGot() {
    let gotAll = true;
    const keys = Object.keys(this.analyticsBody_);
    this.metricNeeded_.forEach(m => {
      if (keys.indexOf(m.toLowerCase()) < 0) {
        gotAll = false;
      }
    });
    return gotAll;
  }

  /**
   * Builds a query string from a JSON object
   * @param {Object} obj
   * @return {string}
   */
  objToQueryString(obj) {
    let ret = '';
    const keys = Object.keys(obj);
    ret = keys.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`).join('&');
    return ret;
  }

  /**
   * Collects dynamic element heights.
   * @return {Object}
   */
  collectElementHeights() {
    const result = {};
    if (!window.ui || !window.ui.webvitals) {
      return result;
    }

    const clsList = window.ui.webvitals.analyzeElementHeights;
    if (clsList) {
      clsList.forEach(clsName => {
        const elementCollection = document.getElementsByClassName(clsName);
        if (elementCollection !== null && elementCollection.length > 0) {
          result[clsName] = /** @type HTMLElement */ (elementCollection[0]).offsetHeight;
        }
      });
    }
    return result;
  }

  /**
   * Collects and sends measured data to back-end
   */
  sendToAnalytics() {
    const qs = this.objToQueryString(this.analyticsBody_);
    let url = this.analyticsUrl_;
    if (url.indexOf('?') > 0) {
      url += `&${qs}`;
    } else {
      url += `?${qs}`;
    }

    const body = {
      'elementHeights': this.collectElementHeights(),
      'clsEntries': this.clsEntries
    };

    const bodyAsString = JSON.stringify(body);
    console.log('[CWV] Details ', bodyAsString);

    // Use `navigator.sendBeacon()` if available, falling back to `fetch()`.
    if (window.navigator.sendBeacon) {
      console.log('[CWV] sendBeacon ');
      if (typeof Blob !== 'undefined') {
        window.navigator.sendBeacon(url, new Blob([bodyAsString], {
          type: 'application/json'
        }));
      } else {
        window.navigator.sendBeacon(url);
      }
    } else {
      console.log('[CWV] fetch ', url);
      fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json'
        },
        body: bodyAsString
      }
      );
    }
  }

  /**
   * Start
   */
  run() {
    this.analyticsUrl_ = this.getAnalyticsUrl();
    if (this.analyticsUrl_) {
      // this.pubSub_.subscribe('ad:gotDeviceClass', params => {
      this.initBody('', Viewport.getLayoutClass());
      try {
        onCLS(metric => this.collectLayoutShift(metric), {reportAllChanges: true});
        onLCP(metric => this.collectMetric(metric), {reportAllChanges: true});
        onTTFB(metric => this.collectTTFB(metric));
        onINP(metric => this.collectMetric(metric), {reportAllChanges: true});
      } catch (e) {
        console.error('Error calling APIs:', e);
      }
      // });
    } else {
      console.log('[CWV] Collecting core web vitals disabled.');
    }
  }
}

export default new WebVitals();
